import Document from '@client-shared/models/document.model'
import LocationLayer from '@client-shared/models/location-layer.model'
import Participant from '@client-shared/models/participant.model'
import PfFile from '@client-shared/models/pf-file.model'
import Photo from '@client-shared/models/photo.model'
import Plan from '@client-shared/models/plan.model'
import Project from '@client-shared/models/project.model'
import Task from '@client-shared/models/task.model'
import generateRandomId from '@client-shared/utils/crypto-random-uuid.js'
import axios from 'axios'
import { io } from 'socket.io-client'

import i18nInstance from '@/plugins/i18n'
import refetchProjectData from '@/utils/refetch-project-data.js'
import sendRaygun from '@/utils/send-raygun.js'

const $t = i18nInstance.global.t

// On the project dashboard the counters come from a single project/fetchDashboardCounts
// call, so anything changing over the socket would otherwise leave them stale. Rather than
// maintaining a list of count-affecting events (which silently rots whenever a new event is
// added), we refetch on any socket event while the dashboard is the active route. The counts
// come from the server and the fetch is cheap, so we just debounce bursts (e.g. multi-file
// uploads emitting many events) into a single refetch.
const DASHBOARD_COUNTS_REFETCH_DEBOUNCE_MS = 1500
let dashboardCountsRefetchTimeout

const refreshDashboardCountsDebounced = ({ app }) => {
  // Skip if the user is not on the project dashboard.
  // When the user visits the dashboard, a XHR GET request updates the counter
  if (app.config.globalProperties.$route.name !== 'projects-projectId') {
    return
  }

  clearTimeout(dashboardCountsRefetchTimeout)

  dashboardCountsRefetchTimeout = setTimeout(() => {
    const route = app.config.globalProperties.$route

    // Skip if the user navigated away from the dashboard during the debounce window.
    if (route.name !== 'projects-projectId') {
      return
    }

    app.config.globalProperties.$store.dispatch('project/fetchDashboardCounts', route.params.projectId)
  }, DASHBOARD_COUNTS_REFETCH_DEBOUNCE_MS)
}

let queuedEvents = []
const socketHandlers = {
  PARTICIPANT_NEW: {
    getResourceId: ({ jsonPayload }) => {
      const { participant } = jsonPayload

      return participant._id
    },
    handler: ({ app, jsonPayload }) => {
      const { participant } = jsonPayload
      const store = app.config.globalProperties.$store

      store.commit('project/ADD_PARTICIPANT', new Participant(participant))
    },
  },

  PARTICIPANT_UPDATE: {
    getResourceId: ({ jsonPayload }) => {
      const { participant } = jsonPayload

      return participant._id
    },
    handler: ({ app, jsonPayload }) => {
      const { participant } = jsonPayload
      const store = app.config.globalProperties.$store

      store.commit('project/UPDATE_PARTICIPANT', new Participant(participant))
    },
  },

  PROJECT_UPDATE: {
    getProjectId: ({ jsonPayload }) => {
      const { project } = jsonPayload

      return project._id
    },
    getResourceId: ({ jsonPayload }) => {
      const { project } = jsonPayload

      return project._id
    },
    handler: ({ app, jsonPayload }) => {
      const { project } = jsonPayload
      const store = app.config.globalProperties.$store

      store.commit('project/SET_PROJECT', new Project(project))
    },
  },

  TASK_NEW: {
    getResourceId: ({ jsonPayload }) => {
      const { task } = jsonPayload

      return task._id
    },
    handler: ({ app, jsonPayload }) => {
      const { task } = jsonPayload
      const store = app.config.globalProperties.$store

      store.commit('tasks/ADD_TASK', new Task(task))
    },
  },

  TASK_UPDATE: {
    getResourceId: ({ jsonPayload }) => {
      const { task } = jsonPayload

      return task._id
    },
    handler: ({ app, jsonPayload }) => {
      const { task } = jsonPayload
      const store = app.config.globalProperties.$store

      store.commit('tasks/ADD_TASK', new Task(task))
      store.commit('activities/INCREMENT_INLINE_TASK_ACTIVITIES_REFRESH_TOKEN')
    },
  },

  TASK_FILE_THUMBNAIL_UPDATE: {
    getResourceId: ({ jsonPayload }) => {
      const { task } = jsonPayload

      return task._id
    },
    handler: ({ app, jsonPayload }) => {
      const { taskId, task } = jsonPayload
      const store = app.config.globalProperties.$store

      const originalTask = store.getters['tasks/getById'](taskId)

      if (!originalTask) {
        console.log(`Original task with _id "${taskId}" couldn't be found`)

        return
      }

      store.commit('tasks/REPLACE_TASK', new Task(task))
    },
  },
  TASK_COMMENT_FILE_THUMBNAIL_UPDATE: {
    getResourceId: ({ jsonPayload }) => {
      const { task } = jsonPayload

      return task._id
    },
    handler: ({ app, jsonPayload }) => {
      const { taskId, task } = jsonPayload
      const store = app.config.globalProperties.$store

      const originalTask = store.getters['tasks/getById'](taskId)

      if (!originalTask) {
        console.error(`Original task with _id "${taskId}" couldn't be found`)

        return
      }

      store.commit('tasks/REPLACE_TASK', new Task(task))
    },
  },

  TASK_COMMENT_NEW: {
    getResourceId: ({ jsonPayload }) => {
      const { task } = jsonPayload

      return task._id
    },
    handler: ({ app, jsonPayload }) => {
      const { task } = jsonPayload
      const store = app.config.globalProperties.$store

      store.commit('tasks/REPLACE_TASK', new Task(task))
    },
  },

  TASK_COMMENT_UPDATE: {
    getResourceId: ({ jsonPayload }) => {
      const { task } = jsonPayload

      return task._id
    },
    handler: ({ app, jsonPayload }) => {
      const { task } = jsonPayload
      const store = app.config.globalProperties.$store

      store.commit('tasks/REPLACE_TASK', new Task(task))
    },
  },

  TASK_REMOVE_VIEW_PERMISSION: {
    getResourceId: ({ jsonPayload }) => {
      const { taskId } = jsonPayload

      return taskId
    },
    handler: ({ app, jsonPayload }) => {
      const { taskId } = jsonPayload
      const store = app.config.globalProperties.$store

      store.commit('tasks/REMOVE_TASK', taskId)

      if (app.config.globalProperties.$route.name === 'projects-projectId-tasks-taskId' && app.config.globalProperties.$route.params.taskId === taskId) {
        alert(`${$t('feature.task.detail.task_not_available')} - ${$t('feature.task.detail.maybe_no_permission_to_view_task')}`)

        app.config.globalProperties.$router.push({
          name: 'projects-projectId-tasks',
          params: {
            projectId: store.state.project.project._id,
          },
        })
      }
    },
  },

  PLAN_NEW: {
    getResourceId: ({ jsonPayload }) => {
      const { plan } = jsonPayload

      return plan._id
    },
    handler: ({ app, jsonPayload }) => {
      const { plan } = jsonPayload
      const store = app.config.globalProperties.$store

      store.commit('plans/ADD_PLAN', new Plan(plan))
    },
  },

  PLAN_UPDATE: {
    getResourceId: ({ jsonPayload }) => {
      const { plan } = jsonPayload

      return plan._id
    },
    handler: ({ app, jsonPayload }) => {
      const { plan } = jsonPayload
      const store = app.config.globalProperties.$store

      store.commit('plans/ADD_PLAN', new Plan(plan))
      store.commit('activities/INCREMENT_INLINE_PLAN_ACTIVITIES_REFRESH_TOKEN')
    },
  },

  PLAN_REVISION_NEW: {
    getResourceId: ({ jsonPayload }) => {
      const { plan } = jsonPayload

      return plan._id
    },
    handler: ({ app, jsonPayload }) => {
      const { plan } = jsonPayload
      const store = app.config.globalProperties.$store

      store.commit('plans/REPLACE_PLAN', new Plan(plan))
    },
  },

  DOCUMENT_NEW: {
    getResourceId: ({ jsonPayload }) => {
      const { document } = jsonPayload

      return document._id
    },
    handler: ({ app, jsonPayload }) => {
      const { document } = jsonPayload
      const store = app.config.globalProperties.$store

      store.commit('documents/ADD_DOCUMENT', new Document(document))
    },
  },

  DOCUMENT_UPDATE: {
    getResourceId: ({ jsonPayload }) => {
      const { document } = jsonPayload

      return document._id
    },
    handler: ({ app, jsonPayload }) => {
      const { document } = jsonPayload
      const store = app.config.globalProperties.$store

      store.commit('documents/ADD_DOCUMENT', new Document(document))
      store.commit('activities/INCREMENT_INLINE_DOCUMENT_ACTIVITIES_REFRESH_TOKEN')
    },
  },

  DOCUMENT_REVISION_NEW: {
    getResourceId: ({ jsonPayload }) => {
      const { document } = jsonPayload

      return document._id
    },
    handler: ({ app, jsonPayload }) => {
      const { document } = jsonPayload
      const store = app.config.globalProperties.$store

      store.commit('documents/REPLACE_DOCUMENT', new Document(document))
    },
  },

  DOCUMENT_REMOVE_VIEW_PERMISSION: {
    getResourceId: ({ jsonPayload }) => {
      const { documentId } = jsonPayload

      return documentId
    },
    handler: ({ app, jsonPayload }) => {
      const { documentId } = jsonPayload
      const store = app.config.globalProperties.$store

      store.commit('documents/REMOVE_DOCUMENT', documentId)

      if (app.config.globalProperties.$route.name === 'projects-projectId-documents-documentId' && app.config.globalProperties.$route.params.documentId === documentId) {
        alert(`${$t('feature.item.document_not_available')} - ${$t('feature.item.maybe_no_permission_to_view_document')}`)

        app.config.globalProperties.$router.push({
          name: 'projects-projectId-documents',
          params: {
            projectId: store.state.project.project._id,
          },
        })
      }
    },
  },

  TRIGGER_RELOAD_PLANS: {
    handler: ({ app, jsonPayload }) => {
      const { projectId } = jsonPayload
      const store = app.config.globalProperties.$store

      // Same loadedProjectId gate used by the reconnect catch-up
      // (cliesrc/utils/refetch-project-data.js): another user's mutation
      // shouldn't force-load a list the current user has never opened.
      if (store.state.plans.loadedProjectId !== projectId) {
        return
      }

      store.dispatch('plans/fetch', projectId)
    },
  },

  TRIGGER_RELOAD_DOCUMENTS: {
    handler: ({ app, jsonPayload }) => {
      const { projectId } = jsonPayload
      const store = app.config.globalProperties.$store

      if (store.state.documents.loadedProjectId !== projectId) {
        return
      }

      store.dispatch('documents/fetch', projectId)
    },
  },

  TRIGGER_RELOAD_TASKS: {
    handler: ({ app, jsonPayload }) => {
      const { projectId } = jsonPayload
      const store = app.config.globalProperties.$store

      if (store.state.tasks.loadedProjectId !== projectId) {
        return
      }

      store.dispatch('tasks/fetch', {
        projectId,
      })
    },
  },

  TRIGGER_RELOAD_PHOTOS: {
    handler: ({ app, jsonPayload }) => {
      const { projectId } = jsonPayload
      const store = app.config.globalProperties.$store

      if (store.state.photos.loadedProjectId !== projectId) {
        return
      }

      store.dispatch('photos/fetch', {
        projectId,
      })
    },
  },

  // No loadedProjectId gate: project/fetch is the precondition for entering
  // the project context (always loaded once we receive socket events for it),
  // and it covers participants + filters + the page header.
  TRIGGER_RELOAD_PROJECT: {
    handler: ({ app, jsonPayload }) => {
      const { projectId } = jsonPayload
      const store = app.config.globalProperties.$store

      // The reloaded project may no longer be accessible (e.g. it was trashed),
      // making this refetch reject with 403. The axios error interceptor already
      // handles logging and the redirect to the projects list, so we swallow the
      // rejection here to avoid an unhandled promise rejection.
      store.dispatch('project/fetch', projectId)
        .catch(() => {})
    },
  },

  TRIGGER_RELOAD_LOCATION_LAYERS: {
    handler: ({ app, jsonPayload }) => {
      const { projectId } = jsonPayload
      const store = app.config.globalProperties.$store

      if (store.state.locationLayers.loadedProjectId !== projectId) {
        return
      }

      store.dispatch('locationLayers/fetch', { projectId })
    },
  },

  // No loadedProjectId gate: filters are part of project/fetch and always
  // loaded in the project context.
  TRIGGER_RELOAD_FILTERS: {
    handler: ({ app, jsonPayload }) => {
      const { projectId } = jsonPayload
      const store = app.config.globalProperties.$store

      store.dispatch('project/fetchFilters', { projectId })
    },
  },

  TRIGGER_RELOAD_PF_FILES: {
    handler: ({ app, jsonPayload }) => {
      const { projectId } = jsonPayload
      const store = app.config.globalProperties.$store

      if (store.state.pfFiles.loadedProjectId !== projectId) {
        return
      }

      store.dispatch('pfFiles/fetch', projectId)
    },
  },

  LOCATION_LAYER_NEW: {
    getResourceId: ({ jsonPayload }) => {
      const { locationLayer } = jsonPayload

      return locationLayer._id
    },
    handler: ({ app, jsonPayload }) => {
      const { locationLayer } = jsonPayload
      const store = app.config.globalProperties.$store

      store.commit('locationLayers/ADD_LOCATION_LAYER', new LocationLayer(locationLayer))
    },
  },

  LOCATION_LAYER_UPDATE: {
    getResourceId: ({ jsonPayload }) => {
      const { locationLayer } = jsonPayload

      return locationLayer._id
    },
    handler: ({ app, jsonPayload }) => {
      const { locationLayer } = jsonPayload
      const store = app.config.globalProperties.$store

      const originalLocationLayer = store.getters['locationLayers/getById'](locationLayer._id)

      // Expected right after creating a layer: the tiling job's first update can arrive before the POST
      // response added the layer. Its later updates land once the layer is in the store.
      if (!originalLocationLayer) {
        console.log(`Original locationLayer with _id "${locationLayer._id}" couldn't be found`)

        return
      }

      store.commit('locationLayers/REPLACE_LOCATION_LAYER', new LocationLayer(locationLayer))
    },
  },

  PF_FILE_NEW: {
    getResourceId: ({ jsonPayload }) => {
      const { pfFile } = jsonPayload

      return pfFile._id
    },
    handler: ({ app, jsonPayload }) => {
      const { pfFile } = jsonPayload
      const store = app.config.globalProperties.$store

      store.commit('pfFiles/ADD_PF_FILE', new PfFile(pfFile))
    },
  },

  PF_FILE_THUMBNAIL_UPDATE: {
    getResourceId: ({ jsonPayload }) => {
      const { pfFile } = jsonPayload

      return pfFile._id
    },
    handler: ({ app, jsonPayload }) => {
      const { pfFile } = jsonPayload
      const store = app.config.globalProperties.$store

      const pfFileModel = new PfFile(pfFile)
      const originalPfFile = store.getters['pfFiles/getById'](pfFile._id)

      if (originalPfFile) {
        store.commit('pfFiles/REPLACE_PF_FILE', pfFileModel)
      } else {
        // Can happen when the event outruns the creation flow (the resize job
        // finished before PF_FILE_NEW or the lazy fetchById put the file in the
        // store) — the payload is the full pfFile and events are already scoped
        // to the current project, so add it instead of losing the thumbnail
        // update. Same as the ionic handler.
        store.commit('pfFiles/ADD_PF_FILE', pfFileModel)
      }
    },
  },

  PHOTO_NEW: {
    getResourceId: ({ jsonPayload }) => {
      const { photo } = jsonPayload

      return photo._id
    },
    handler: ({ app, jsonPayload }) => {
      const { photo } = jsonPayload
      const store = app.config.globalProperties.$store

      store.commit('photos/ADD_PHOTO', new Photo(photo))
    },
  },

  PHOTO_UPDATE: {
    getResourceId: ({ jsonPayload }) => {
      const { photo } = jsonPayload

      return photo._id
    },
    handler: ({ app, jsonPayload }) => {
      const { photo } = jsonPayload
      const store = app.config.globalProperties.$store

      store.commit('photos/ADD_PHOTO', new Photo(photo))
    },
  },

  // The async print-job PDF render writes its outcome to printJob.pdfGeneration
  // and pushes it here when it finishes. getResourceId returns the printJob._id
  // (never a popup-locked id — the print overlay locks nothing) so the event is
  // applied directly instead of being queued behind the open print overlay.
  PRINT_JOB_UPDATE: {
    getResourceId: ({ jsonPayload }) => {
      const { printJob } = jsonPayload

      return printJob._id
    },
    handler: ({ app, jsonPayload }) => {
      const { printJob } = jsonPayload
      const store = app.config.globalProperties.$store

      store.commit('printJobs/UPSERT_PRINT_JOB', printJob)
    },
  },
}

// Global guard: ignore any socket event whose project doesn't match the currently
// loaded project, instead of repeating this check inside every handler. Most events
// carry a top-level `projectId`; handlers whose payload identifies the project
// differently expose a `getProjectId` resolver (e.g. PROJECT_UPDATE).
function isEventForCurrentProject ({ store, eventName, jsonPayload }) {
  const handler = socketHandlers[eventName]
  const eventProjectId = handler.getProjectId
    ? handler.getProjectId({ jsonPayload })
    : jsonPayload.projectId

  return eventProjectId === store.state?.project?.project?._id
}

// function logPopupContextUpdate ({ app, socketAction, socketPayload, updatedContextId }) {
//   const store = app.config.globalProperties.$store
//   const logger = app.config.globalProperties.$logger

//   const firstPopupWithContext = store.state.layout.openPopups.find(popup => popup.lockedIds.includes(updatedContextId))

//   if (!firstPopupWithContext) {
//     return
//   }

//   logger.log({
//     tag: 'POPUP_CONTEXT_UPDATED_VIA_SOCKET',
//     context: {
//       socketAction,
//       socketPayload,
//       updatedContextId,
//       openPopups: store.state.layout.openPopups.map(popup => {
//         return {
//           popupId: popup.popupId,
//           popupType: popup.popupType,
//           lockedIds: popup.lockedIds,
//           componentName: popup.componentName,
//         }
//       }),
//     },
//   })
// }

// function logListReload ({ app, socketAction, projectId }) {
//   const store = app.config.globalProperties.$store
//   const logger = app.config.globalProperties.$logger

//   const hasOpenPopup = store.state.layout.openPopups.length > 0

//   if (!hasOpenPopup) {
//     return
//   }

//   logger.log({
//     tag: 'TRIGGER_RELOAD_VIA_SOCKET',
//     context: {
//       socketAction,
//       socketPayload: {
//         projectId,
//       },
//       openPopups: store.state.layout.openPopups.map(popup => {
//         return {
//           popupId: popup.popupId,
//           popupType: popup.popupType,
//           lockedIds: popup.lockedIds,
//           componentName: popup.componentName,
//         }
//       }),
//     },
//   })
// }

// Delay between manual reconnect attempts after the server forced a disconnect.
const SOCKET_RECONNECT_RETRY_DELAY_MS = 3000

export default {
  install: (app) => {
    let ioConnection
    let isConnected = false
    let currentProjectId = null
    // Tracks whether the active socket has dropped at least once. Set on
    // 'disconnect' and cleared after the catch-up refetch runs, so a fresh
    // first-time 'connect' (which always fires) doesn't trigger an unneeded
    // refetch on top of the initial XHR loads.
    let wasDisconnected = false
    let reconnectTimer = null

    const clearReconnectTimer = () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer)
        reconnectTimer = null
      }
    }

    const scheduleManualReconnect = () => {
      // Idempotent: 'io server disconnect' and the connect_error storm that
      // follows can both call this in quick succession. Bail if a retry is
      // already pending so we don't reset the 2-second window each time.
      if (reconnectTimer) {
        return
      }

      console.log(`Socket connection lost - retrying in ${SOCKET_RECONNECT_RETRY_DELAY_MS}ms …`)
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null

        if (!ioConnection || isConnected) {
          return
        }

        ioConnection.connect()
        scheduleManualReconnect()
      }, SOCKET_RECONNECT_RETRY_DELAY_MS)
    }

    const socket = {
      connect (projectId) {
        if (app.config.globalProperties.$config.isBackupApp) {
          return
        }

        if (!projectId) {
          console.error('No projectId passed to connect to websocket')

          return
        }

        // Already connected to this exact project
        if (isConnected && currentProjectId === projectId) {
          return
        }

        // A previous server kick is still running its manual reconnect loop for
        // this same project (ioConnection alive, isConnected=false). Tearing
        // the socket down here would clearReconnectTimer() the pending retry,
        // and the fresh ioConnection that would replace it has no path back
        // into scheduleManualReconnect() — only the 'io server disconnect'
        // branch triggers it, and a freshly-created socket that never
        // connected can't fire 'disconnect'. Leave the loop running.
        if (ioConnection && !isConnected && currentProjectId === projectId) {
          return
        }

        // Switching projects (or recovering from a stale attempt): tear down first
        socket.disconnect()

        currentProjectId = projectId

        const connectStartedAt = performance.now()

        // Fire-and-forget: establishing the connection never blocks navigation/rendering. Live updates that
        // fire between a page's initial XHR load and the socket joining the room are recovered by the
        // reconnect/refetch logic below.
        ioConnection = io.connect(app.config.globalProperties.$config.api.websocketServerUrl, {
          transports: [ 'websocket' ],
          auth: {
            token: app.config.globalProperties.$auth.accessToken,
            projectId,
          },
        })

        ioConnection.on('connect', () => {
          isConnected = true
          clearReconnectTimer()
          console.log(`Socket connection established in ${Math.round(performance.now() - connectStartedAt)}ms`)

          // Recover from missed events: while the socket was down no
          // *_NEW / *_UPDATE / TRIGGER_RELOAD_* events arrived, so lists
          // that are only kept fresh via socket pushes are stale. Refetch
          // them — only on a true reconnect, not on the initial connect.
          if (wasDisconnected) {
            wasDisconnected = false
            refetchProjectData({
              store: app.config.globalProperties.$store,
              route: app.config.globalProperties.$route,
            })
              // A failed request is already reported by the axios error interceptor (a canceled one is
              // no failure) - letting it reject unhandled only reported it a second time. Anything else
              // is a bug in the catch-up and still has to reach Raygun.
              .catch(error => {
                if (axios.isAxiosError(error)) {
                  return
                }

                sendRaygun({
                  error,
                  tags: [ 'socket-reconnect-refetch' ],
                })
              })
          }
        })

        ioConnection.on('disconnect', reason => {
          console.log(`Socket connection lost - disconnect event (reason: ${reason})`)
          isConnected = false
          wasDisconnected = true

          // socket.io-client auto-reconnects for transport-level drops, but not
          // when the server forces a disconnect ('io server disconnect') — in
          // that case we have to dial back manually and keep retrying until the
          // server accepts us again, otherwise the socket stays dead even after
          // the server comes back up.
          if (reason === 'io server disconnect') {
            scheduleManualReconnect()
          }
        })

        ioConnection.on('connect_error', () => {
          // socket.io-client's own reconnection runs after connect_error for
          // transport-level failures, but both Chrome and Firefox throttle
          // repeated failing WebSocket handshakes against the same origin and
          // eventually stop firing connect_error too — at which point a
          // fresh ioConnection goes silent. This also covers an ioConnection
          // created by a navigation while the server was already down: it
          // never reaches the 'io server disconnect' branch on 'disconnect'
          // because it never connected. Our manual reconnect cadence is the fallback.
          if (!isConnected) {
            scheduleManualReconnect()
          }
        })

        ioConnection.onAny((eventName, payload) => {
        // If the eventhandler has a resourceId, check if there is any popup open with this resourceid. If yes, push the update into a queue and don't apply it directly. If no, apply the update directly.
        // If the eventhandler has no resourceId (e.g. TRIGGER_RELOAD_PLANS, TRIGGER_RELOAD_DOCUMENTS, TRIGGER_RELOAD_TASKS, TRIGGER_RELOAD_PROJECT, TRIGGER_RELOAD_LOCATION_LAYERS, TRIGGER_RELOAD_FILTERS) we push the event to the queue if a popup is open

          const handler = socketHandlers[eventName]
          const jsonPayload = JSON.parse(payload)

          if (!handler) {
            console.warn(`No handler found for event: ${eventName}`)

            return
          }

          const store = app.config.globalProperties.$store

          // Drop events that don't belong to the currently loaded project.
          if (!isEventForCurrentProject({
            store, eventName, jsonPayload,
          })) {
            return
          }

          refreshDashboardCountsDebounced({ app })

          const resourceId = handler.getResourceId ? handler.getResourceId({ jsonPayload }) : undefined
          const hasLockedContext = resourceId && store.getters['layout/getLockedIds'].includes(resourceId)
          const hasOpenPopup = store.state.layout.openPopups.length > 0

          if (hasLockedContext || (!resourceId && hasOpenPopup)) {
          // Remove old event from queue and push new one to the end of the queue. Otherwise there might be cases where old events override a new state
            queuedEvents = queuedEvents.filter(event => {
              if (resourceId) {
                return event.resourceId !== resourceId && event.eventName !== eventName
              }

              return !event.resourceId && event.eventName !== eventName
            })

            queuedEvents.push({
              id: generateRandomId(),
              eventName,
              resourceId,
              jsonPayload,
            })
          } else {
            handler.handler({
              app,
              jsonPayload,
            })
          }
        })
      },

      disconnect () {
        currentProjectId = null
        isConnected = false
        clearReconnectTimer()
        // Deliberate tear-down (project switch, logout, navigation out of
        // project context). The next connect() establishes a brand-new socket
        // whose 'connect' is an initial connect — not a reconnect — so it must
        // not trigger the catch-up refetch.
        wasDisconnected = false

        // Tear down whenever a connection object exists, not only when it has
        // finished connecting: an in-flight attempt (or socket.io auto-reconnect)
        // keeps ioConnection alive while isConnected is still false. Guarding on
        // isConnected here would leak that socket and its listeners, leaving
        // multiple simultaneous connections when switching projects mid-connect.
        if (!ioConnection) {
          return
        }

        ioConnection.off()
        ioConnection.disconnect()
        ioConnection = null
      },

      getConnection () {
        return ioConnection
      },

      applyQueueUpdates () {
        // A popup (modal, overlay) has a context, like a taskId. If a popup with a specific contextId is open, and a websocket message with this exact contextId appears, we don't apply the update directly. Instead we push it into a queue and make the update as soon as the popup got closed. This way we prevent modals to be closed unexpectedly, because of a disappearing list-item caused by a filter result set change.

        for (const event of queuedEvents) {
          const handler = socketHandlers[event.eventName]
          const store = app.config.globalProperties.$store

          // Drop queued events that no longer belong to the currently loaded project.
          if (!isEventForCurrentProject({
            store, eventName: event.eventName, jsonPayload: event.jsonPayload,
          })) {
            queuedEvents = queuedEvents.filter(e => e.id !== event.id)

            continue
          }

          const hasLockedContext = event.resourceId && store.getters['layout/getLockedIds'].includes(event.resourceId)
          const hasOpenPopup = store.state.layout.openPopups.length > 0

          if (hasLockedContext || (!event.resourceId && hasOpenPopup)) {
            continue
          }

          handler.handler({
            app,
            jsonPayload: event.jsonPayload,
          })

          queuedEvents = queuedEvents.filter(e => e.id !== event.id)
        }
      },
    }

    app.config.globalProperties.$socket = socket

    // Own the navigation-driven socket lifecycle here instead of in a layout watcher.
    // connect() is fire-and-forget: it must never block navigation or rendering, so we don't await it -
    // a slow or unreachable websocket server has no effect on how fast the app paints. Live updates that
    // fire between a page's initial XHR load and the socket joining the room are recovered by the
    // reconnect/refetch logic in connect().
    // This guard registers after the auth plugin's guard (socket-io is installed after
    // auth), so the user/token are already resolved when it runs. It also registers
    // after the unsaved-changes guard (see main.js install order): if that guard aborts
    // the navigation, Vue Router never runs this one, so we don't switch socket rooms
    // for a navigation that ends up cancelled.
    const $router = app.config.globalProperties.$router

    $router.beforeEach((to) => {
      const isLoggedIn = Boolean(app.config.globalProperties.$auth && app.config.globalProperties.$auth.loggedIn)
      const isProjectPage = Boolean(to.name && to.name.startsWith('projects-projectId'))

      if (!isLoggedIn || !isProjectPage) {
        socket.disconnect()

        return
      }

      socket.connect(to.params.projectId)
    })
  },
}
